import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { featureApi, MOCK_MODE } from '../lib';
import { useCurrentProject } from './use-current-project';

export interface LeaderboardConfig {
  id: string;
  projectId: string;
  name: string;
  metricField: string;
  sortOrder: string;
  displayLimit: number;
  timeWindow: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateLeaderboardConfigDto {
  name: string;
  metricField?: string;
  sortOrder?: string;
  displayLimit?: number;
  timeWindow?: string;
  isActive?: boolean;
}

export interface UpdateLeaderboardConfigDto {
  name?: string;
  metricField?: string;
  sortOrder?: string;
  displayLimit?: number;
  timeWindow?: string;
  isActive?: boolean;
}

const MOCK_CONFIGS: LeaderboardConfig[] = [
  {
    id: '1',
    projectId: 'proj-1',
    name: 'Points Leaders',
    metricField: 'points',
    sortOrder: 'desc',
    displayLimit: 100,
    timeWindow: 'all-time',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: '2',
    projectId: 'proj-1',
    name: 'Weekly Champions',
    metricField: 'points',
    sortOrder: 'desc',
    displayLimit: 10,
    timeWindow: 'weekly',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

const configKeys = {
  all: ['leaderboard-configs'] as const,
  list: (projectId: string) => [...configKeys.all, projectId] as const,
};

export function useLeaderboardConfigs() {
  const { projectId } = useCurrentProject();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: configKeys.list(projectId ?? ''),
    queryFn: async () => {
      if (MOCK_MODE) return MOCK_CONFIGS;
      return featureApi.get<LeaderboardConfig[]>(`/points/leaderboard/configs`);
    },
    enabled: !!projectId,
    staleTime: 1000 * 30,
  });

  const createMutation = useMutation({
    mutationFn: async (dto: CreateLeaderboardConfigDto) => {
      if (MOCK_MODE) {
        const newConfig: LeaderboardConfig = {
          id: Date.now().toString(),
          projectId: projectId ?? '',
          name: dto.name,
          metricField: dto.metricField ?? 'points',
          sortOrder: dto.sortOrder ?? 'desc',
          displayLimit: dto.displayLimit ?? 100,
          timeWindow: dto.timeWindow ?? 'all-time',
          isActive: dto.isActive ?? true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        return newConfig;
      }
      return featureApi.post<LeaderboardConfig>('/points/leaderboard/configs', dto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: configKeys.list(projectId ?? '') });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ configId, dto }: { configId: string; dto: UpdateLeaderboardConfigDto }) => {
      if (MOCK_MODE) {
        return { id: configId, ...dto } as LeaderboardConfig;
      }
      return featureApi.patch<LeaderboardConfig>(`/points/leaderboard/configs/${configId}`, dto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: configKeys.list(projectId ?? '') });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (configId: string) => {
      if (MOCK_MODE) return;
      return featureApi.delete(`/points/leaderboard/configs/${configId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: configKeys.list(projectId ?? '') });
    },
  });

  return {
    configs: query.data ?? [],
    isLoading: query.isLoading,
    createConfig: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    updateConfig: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteConfig: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}
